// utils/getMenuItems.js
import {
  PencilSquareIcon,
  ShareIcon,
  FlagIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

/**
 * Generate context-aware menu items for notes based on ownership and visibility.
 * Private notes never expose a Share entry — sharing a link to a note that
 * other viewers can't actually open would be misleading UX.
 *
 * @param {boolean} isOwner - Whether current user owns the note
 * @param {string} visibility - Note visibility ("public" | "private" | etc.)
 * @param {Object} handlers - Handler functions for each action
 * @param {function} handlers.onEdit - Edit handler
 * @param {function} handlers.onShare - Share handler
 * @param {function} handlers.onReport - Report handler
 * @param {function} handlers.onDelete - Delete handler
 * @returns {Array} Array of menu item objects with label, icon, onSelect
 */
export function getNoteMenuItems(isOwner, visibility, handlers = {}) {
  const { onEdit, onShare, onReport, onDelete } = handlers;
  const canShare = visibility !== "private";

  const shareItem = canShare
    ? [
        {
          label: "Share",
          icon: <ShareIcon className="h-4 w-4" />,
          onSelect: onShare || (() => {}),
        },
      ]
    : [];

  if (isOwner) {
    // Owner can: Edit, (Share if public), Delete
    return [
      {
        label: "Edit",
        icon: <PencilSquareIcon className="h-4 w-4" />,
        onSelect: onEdit || (() => {}),
      },
      ...shareItem,
      {
        label: "Delete",
        icon: <TrashIcon className="h-4 w-4 text-destructive" />,
        onSelect: onDelete || (() => {}),
      },
    ];
  } else {
    // Non-owner can: (Share if public), Report
    return [
      ...shareItem,
      {
        label: "Report",
        icon: <FlagIcon className="h-4 w-4" />,
        onSelect: onReport || (() => {}),
      },
    ];
  }
}

/**
 * Generate context-aware menu items for comments based on ownership
 *
 * @param {boolean} isCommentOwner - Whether current user owns the comment
 * @param {boolean} isNoteOwner - Whether current user owns the note (parent)
 * @param {Object} handlers - Handler functions for each action
 * @param {function} handlers.onEdit - Edit handler
 * @param {function} handlers.onReport - Report handler
 * @param {function} handlers.onDelete - Delete handler
 * @returns {Array} Array of menu item objects with label, icon, onSelect
 */
export function getCommentMenuItems(
  isCommentOwner,
  isNoteOwner,
  handlers = {},
) {
  const { onEdit, onReport, onDelete } = handlers;

  if (isCommentOwner) {
    // Comment owner can: Edit, Delete
    return [
      {
        label: "Edit",
        icon: <PencilSquareIcon className="h-4 w-4" />,
        onSelect: onEdit || (() => {}),
      },
      {
        label: "Delete",
        icon: <TrashIcon className="h-4 w-4 text-destructive" />,
        onSelect: onDelete || (() => {}),
      },
    ];
  } else if (isNoteOwner) {
    // Note owner (but not comment owner) can: Report, Delete
    return [
      {
        label: "Report",
        icon: <FlagIcon className="h-4 w-4" />,
        onSelect: onReport || (() => {}),
      },
      {
        label: "Delete",
        icon: <TrashIcon className="h-4 w-4 text-destructive" />,
        onSelect: onDelete || (() => {}),
      },
    ];
  } else {
    // Neither owner can: Report only
    return [
      {
        label: "Report",
        icon: <FlagIcon className="h-4 w-4" />,
        onSelect: onReport || (() => {}),
      },
    ];
  }
}
